import { Dropdown, MultiSelect } from '@carbon/react';
import { ENTITY_TYPE_ITEMS, type EntityType } from 'lib/entities';
import { cleanupFiltersAtom, EMPTY_FILTERS } from './atoms';
import { PROVENANCE_ITEMS, provenanceApplies } from './utils';
import { useLogFilters } from '../hooks';
import {
  LogDateRangeFilter,
  LogResetButton,
  LogSearchFilter,
} from '../log-filters';
import shared from '../admin-log.module.scss';

/**
 * The filter bar above the clean-up log: entity type, Imported provenance,
 * action date range and entity-id search. Any change returns to the first page.
 * @return {JSX.Element} The filter bar
 */
export function CleanupFilters() {
  const { filters, patchFilter, resetKey, reset } = useLogFilters(
    cleanupFiltersAtom,
    EMPTY_FILTERS
  );

  const provenanceEnabled = provenanceApplies(filters.entityTypes);

  return (
    <div className={shared.filterBar}>
      <MultiSelect<EntityType>
        id="cleanup-entity-types"
        className={shared.filterField}
        titleText="Entity type"
        label="All types"
        items={ENTITY_TYPE_ITEMS}
        itemToString={(item) => item ?? ''}
        selectedItems={filters.entityTypes}
        onChange={({ selectedItems }) => {
          const entityTypes = [...(selectedItems ?? [])];
          // A provenance left set while Imported is out of scope would
          // silently empty the log.
          patchFilter(
            provenanceApplies(entityTypes)
              ? { entityTypes }
              : { entityTypes, provenance: '' }
          );
        }}
      />
      <Dropdown
        id="cleanup-provenance"
        className={shared.filterField}
        titleText="Imported provenance"
        label="All provenance"
        disabled={!provenanceEnabled}
        items={PROVENANCE_ITEMS}
        itemToString={(item) => item?.label ?? ''}
        selectedItem={
          PROVENANCE_ITEMS.find((i) => i.id === filters.provenance) ??
          PROVENANCE_ITEMS[0]
        }
        onChange={({ selectedItem }) =>
          patchFilter({ provenance: selectedItem?.id ?? '' })
        }
      />
      <LogDateRangeFilter
        key={`range-${resetKey}`}
        idPrefix="cleanup"
        after={filters.after}
        before={filters.before}
        onChange={patchFilter}
      />
      <LogSearchFilter
        key={`search-${resetKey}`}
        id="cleanup-search"
        labelText="Search (retired or surviving entity id)"
        placeholder="e.g. 12344"
        value={filters.search}
        onChange={(search) => patchFilter({ search })}
      />
      <LogResetButton onReset={reset} />
    </div>
  );
}
